/**
 * Device Type to SVG Icon Path Mapping
 *
 * Maps device types/names to SVG icon file paths.
 * Icons use currentColor to accept backend connectivity_color.
 */
export const DEVICE_ICONS = {
  // Omron Wrist BPM — kept first so partial matching hits "omron" before "bpm"
  "omron wrist bpm - 7 series": "omron-wrist-bpm",
  "omron": "omron-wrist-bpm",

  // Blood Pressure Monitors (BPM)
  "tenovi bpm - l": "blood-pressure",
  "tenovi bpm - s": "blood-pressure",
  "tenovi cellular bpm - l": "blood-pressure",
  "tenovi cellular bpm - s": "blood-pressure",
  "bpm": "blood-pressure",
  "blood pressure": "blood-pressure",
  "blood_pressure": "blood-pressure",
  "bp": "blood-pressure",
  "blood pressure monitor": "blood-pressure",
  "sphygmomanometer": "blood-pressure",

  // Glucose Meters
  "tenovi glucometer": "glucose-meter",
  "tenovi cellular glucometer": "glucose-meter",
  "glucometer": "glucose-meter",
  "glucose meter": "glucose-meter",
  "glucose-meter": "glucose-meter",
  "glucose_meter": "glucose-meter",
  "glucose": "glucose-meter",
  "blood glucose": "glucose-meter",

  // Pulse Oximeters
  "tenovi pulse ox": "pulse-oximeter",
  "tenovi cellular pulse ox": "pulse-oximeter",
  "pulse ox": "pulse-oximeter",
  "pulse-ox": "pulse-oximeter",
  "pulse oximeter": "pulse-oximeter",
  "pulse_oximeter": "pulse-oximeter",
  "spo2": "pulse-oximeter",
  "oximeter": "pulse-oximeter",

  // Scales
  "tenovi scale": "scale",
  "tenovi cellular scale": "scale",
  "bariatric scale": "scale",
  "scale": "scale",
  "weight scale": "scale",
  "weight_scale": "scale",
  "body weight": "scale",

  // Peak Flow Meter
  "tenovi peak flow meter": "amplifier",
  "peak flow meter": "amplifier",
  "peak flow": "amplifier",

  // Thermometer
  "tenovi thermometer": "thermometer",
  "thermometer": "thermometer",
  "temperature": "thermometer",

  // Watch
  "tenovi watch": "watch",
  "watch": "watch",
  "smartwatch": "watch",

  // Hub
  "tenovi hub": "hub",
  "hub": "hub",
  "tenovi_hub": "hub",
  "cellular hub": "hub",
  "wifi_hub": "hub",
  "gateway": "hub",
  "network switch": "hub",

  // Generic Medical Device (fallback)
  "device": "healthcare-medical",
  "medical device": "healthcare-medical",
  "monitor": "healthcare-medical",
};

/**
 * Get SVG icon filename for a device
 * @param {string} deviceName - The name of the device
 * @returns {string} - SVG icon filename (without .svg extension)
 */
export function getDeviceIcon(deviceName) {
  if (!deviceName) return "healthcare-medical"; // Default to generic

  // Try exact match first (normalized)
  const normalizedName = deviceName.toLowerCase().trim();
  if (DEVICE_ICONS[normalizedName]) {
    return DEVICE_ICONS[normalizedName];
  }

  // Try partial match
  for (const [key, icon] of Object.entries(DEVICE_ICONS)) {
    if (normalizedName.includes(key)) {
      return icon;
    }
  }

  // Default fallback
  return "healthcare-medical";
}

/**
 * Get display label for a device
 * @param {string} deviceName - The name of the device
 * @returns {string} - Human-readable label
 */
export function getDeviceLabel(deviceName) {
  if (!deviceName) return "Device";

  // Return the device name as-is for Tenovi devices (they have proper labels)
  return deviceName;
}

const DEVICE_ICON_BASE = "/assets/images/svgs/device-icons/";

// Module-level cache of device-icon SVG markup, keyed by icon name. There are
// only ~9 device icons, but DeviceIconList renders once per patient row, so
// without this each of hundreds of rows would re-fetch the same files. Caching
// the in-flight promise also dedupes the concurrent first-render burst, so each
// icon is fetched at most once per session (SVGs are static).
const deviceIconSvgCache = new Map();

/**
 * Fetch (and cache) a device-icon SVG's raw markup so it can be inlined/recolored.
 * @param {string} iconName - icon filename without extension (from getDeviceIcon)
 * @returns {Promise<string>} the SVG markup, or "" if it could not be loaded
 */
export function loadDeviceIconSvg(iconName) {
  if (!deviceIconSvgCache.has(iconName)) {
    deviceIconSvgCache.set(
      iconName,
      fetch(`${DEVICE_ICON_BASE}${iconName}.svg`)
        .then((res) => (res.ok ? res.text() : ""))
        .catch(() => {
          // Drop the failed entry so a later mount can retry.
          deviceIconSvgCache.delete(iconName);
          return "";
        })
    );
  }
  return deviceIconSvgCache.get(iconName);
}
